import React, { useState, useRef } from "react";
import GradientButton from "../../components/button/Button";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import "./BulkInviteModal.css";

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFilter: (filters: {
    medium: string;
    contacts: string[];
    message: string;
  }) => void;
}

const BulkInviteModal: React.FC<InviteModalProps> = ({
  isOpen,
  onClose,
  onFilter,
}) => {
  const [inviteMedium, setInviteMedium] = useState("email");
  const [contactInput, setContactInput] = useState("");
  const [contacts, setContacts] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [csvContacts, setCsvContacts] = useState<string[]>([]);
  const [csvUploaded, setCsvUploaded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleMediumChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setInviteMedium(e.target.value);
    setContactInput("");
    setContacts([]);
    setCsvContacts([]);
    setCsvUploaded(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset file input
    }
  };

  const handleAddContact = (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();
    if (!contactInput.trim()) return;
    if (contacts.includes(contactInput.trim())) return;
    setContacts([...contacts, contactInput.trim()]);
    setContactInput("");
  };

  const handleRemoveContact = (item: string) => {
    setContacts(contacts.filter((c) => c !== item));
    setCsvContacts(csvContacts.filter((c) => c !== item));
  };

  // CSV upload and extraction
  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      const text = evt.target?.result as string;

      // Split CSV into lines
      const lines = text.split(/\r?\n/);
      let extracted: string[] = [];

      // Flexible phone regex (handles +, 00, brackets, spaces, dashes, extensions)
      const phoneRegex =
        /(?:\+?\d{1,4}[\s-.]*)?(?:\(?\d{1,4}\)?[\s-.]*)?\d{3,4}[\s-.]?\d{3,4}(?:\s*(?:x|ext\.?)\s*\d+)?/i;

      // Simple email regex
      const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

      lines.forEach((line) => {
        const cols = line.split(",");

        cols.forEach((col) => {
          const val = col.trim();
          if (!val) return;

          if (inviteMedium === "email") {
            // Extract emails only
            if (emailRegex.test(val)) extracted.push(val);
          } else {
            // Extract phone numbers
            if (phoneRegex.test(val)) extracted.push(val);
          }
        });
      });

      // Clean duplicates
      extracted = Array.from(new Set(extracted)).filter(Boolean);

      // Save to state
      setCsvContacts(extracted);
      setCsvUploaded(true);

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    reader.readAsText(file);
  };

  const handleFilter = async () => {
    setLoading(true);
    const allContacts = Array.from(new Set([...contacts, ...csvContacts]));
    const payload = {
      customers: allContacts.map((contact) =>
        inviteMedium === "email"
          ? { email: contact }
          : { phone_number: contact }
      ),
      channel: inviteMedium === "email" ? "email" : "sms",
      message,
    };

    try {
      const res = await axiosInstance.post("/customers/invitations", payload);
      toast.success(res.data?.message || "Invitations sent!");
      setContacts([]);
      setCsvContacts([]);
      setMessage("");
      setCsvUploaded(false);
      onClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to send invitations."
      );
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="orders-modal">
      <div className="orders-modal__overlay" onClick={onClose}></div>
      <div className="orders-modal__content">
        <div className="orders__modal__header">
          <h2 className="orders-modal__title">Invite</h2>
          <button className="orders-modal__close" onClick={onClose}>
            &times;
          </button>
        </div>
        <form className="orders-modal__form" onSubmit={handleAddContact}>
          <div className="orders-modal__form-group full">
            <label htmlFor="inviteMedium">Invite Medium</label>
            <select
              id="inviteMedium"
              value={inviteMedium}
              onChange={handleMediumChange}
            >
              <option value="email">Email</option>
              <option value="phone">Phone Number</option>
            </select>
          </div>

          <div className="orders-modal__form-group full">
            {/* <label htmlFor="contact">
              {inviteMedium === "email" ? "Email Address" : "Phone Number"}
            </label> */}
            {/* <div className="BulkInvite-modal__input-row">
              <input
                type={inviteMedium === "email" ? "email" : "tel"}
                id="contact"
                name="contact"
                placeholder={
                  inviteMedium === "email"
                    ? "Enter email"
                    : "Enter phone number"
                }
                value={contactInput}
                onChange={(e) => setContactInput(e.target.value)}
              />
              <GradientButton
                text="Add"
                onClick={() => handleAddContact()}
                disabled={!contactInput.trim()}
              />
            </div> */}
          </div>

          <div className="orders-modal__form-group full">
            <label htmlFor="csv-upload">Upload CSV</label>
            <input
              type="file"
              id="csv-upload"
              accept=".csv"
              onChange={handleCsvUpload}
              style={{ marginTop: 8 }}
              ref={fileInputRef}
            />
            {csvUploaded && (
              <div style={{ marginTop: 8 }}>
                <strong style={{ color: "green" }}>
                  Extracted {csvContacts.length}{" "}
                  {inviteMedium === "email" ? "Emails" : "Phone Numbers"}
                </strong>
                <ul className="BulkInvite-modal__contacts-list">
                  {csvContacts.map((item, idx) => (
                    <li key={idx} className="BulkInvite-modal__contact-item">
                      <span>{item}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveContact(item)}
                        style={{
                          background: "#f44336",
                          color: "#fff",
                          border: "none",
                          borderRadius: "50%",
                          width: 18, // reduced from 22
                          height: 18, // reduced from 22
                          cursor: "pointer",
                          fontWeight: "bold",
                          fontSize: 10, // keep small font
                          lineHeight: "18px", // match new height
                          textAlign: "center",
                          marginLeft: 8,
                          padding: 0,
                        }}
                      >
                        &times;
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </form>

        {/* List of manually added contacts */}
        {contacts.length > 0 && (
          <div className="orders-modal__form-group full">
            <label>
              Added {inviteMedium === "email" ? "Emails" : "Phone Numbers"}
            </label>
            <ul className="BulkInvite-modal__contacts-list">
              {contacts.map((item, idx) => (
                <li key={idx} className="BulkInvite-modal__contact-item">
                  <span>{item}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveContact(item)}
                    style={{
                      background: "#f44336",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: 22,
                      height: 22,
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontSize: 14,
                      lineHeight: "22px",
                      textAlign: "center",
                    }}
                  >
                    &times;
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* <div className="orders-modal__form-group" style={{ width: "100%" }}>
          <label htmlFor="message">Additional Text (optional)</label>
          <textarea
            id="message"
            name="message"
            placeholder="Your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={{
              width: "100%",
              padding: 4,
              resize: "none",
              outline: "none",
              borderRadius: 5,
              border: "1px solid #ccc",
              height: 100,
            }}
          />
        </div> */}

        <div className="orders-modal__actions">
          <div className="orders-modal__apply">
            <GradientButton
              onClick={handleFilter}
              text={loading ? "Sending..." : "Send"}
              disabled={
                (contacts.length === 0 && csvContacts.length === 0) || loading
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkInviteModal;
